import { useRef, useState } from "react";
import { Mail, Phone, MessageCircle, Instagram, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { BtnLink, Reveal } from "@/components/site/Primitives";
import { PageHero } from "@/components/site/Blocks";
import { OfficeSection } from "@/components/site/OfficeSection";
import { CONTACT, useLink, usePage, useT } from "@/lib/i18n";
import { api, errCode } from "@/lib/api";

const field = "mt-2 w-full rounded-2xl border border-forest/20 bg-white/70 px-5 py-4 text-base text-forest outline-none transition-colors focus:border-gold-deep";

const ContactForm = () => {
  const t = useT();
  const { lang } = usePage();
  const c = t.contact;
  const started = useRef(Date.now());
  const [v, setV] = useState({ full_name: "", email: "", message: "", consent: false, website: "" });
  const [state, setState] = useState({ sending: false, done: false, error: "" });
  const set = (k) => (e) => setV({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const er = t.apply.errors;
    if (v.full_name.trim().length < 2) return setState({ ...state, error: er.name });
    if (!/^\S+@\S+\.\S+$/.test(v.email)) return setState({ ...state, error: er.email });
    if (v.message.trim().length < 10) return setState({ ...state, error: er.situation });
    if (!v.consent) return setState({ ...state, error: er.consent });
    setState({ sending: true, done: false, error: "" });
    try {
      await api.post("/contact", { lang, full_name: v.full_name, email: v.email, message: v.message, consent_privacy: v.consent, website: v.website, started_at: started.current });
      setState({ sending: false, done: true, error: "" });
    } catch (err) {
      const code = errCode(err);
      setState({ sending: false, done: false, error: code === "rate_limited" ? er.rate : code === "too_fast" ? er.fast : er.generic });
    }
  };

  if (state.done) {
    return <p data-testid="contact-success" className="flex items-start gap-3 rounded-2xl bg-forest/5 p-6 text-forest"><CheckCircle2 className="shrink-0 text-gold-text" /> {c.success}</p>;
  }
  return (
    <form onSubmit={submit} noValidate data-testid="contact-form" className="space-y-5">
      <input type="text" name="website" value={v.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      <label className="block text-sm font-medium text-forest">{c.name}<input data-testid="contact-name-input" className={field} value={v.full_name} onChange={set("full_name")} autoComplete="name" /></label>
      <label className="block text-sm font-medium text-forest">{c.emailField}<input data-testid="contact-email-input" type="email" className={field} value={v.email} onChange={set("email")} autoComplete="email" /></label>
      <label className="block text-sm font-medium text-forest">{c.message}<textarea data-testid="contact-message-input" rows={5} className={field} value={v.message} onChange={set("message")} /></label>
      <label className="flex items-start gap-3 text-sm text-forest/80">
        <input data-testid="contact-consent-checkbox" type="checkbox" checked={v.consent} onChange={set("consent")} className="mt-0.5 h-5 w-5 accent-[#17382E]" />
        {c.consent}
      </label>
      {state.error && <p role="alert" data-testid="contact-error" className="text-sm text-red-800">{state.error}</p>}
      <button type="submit" disabled={state.sending} data-testid="contact-submit-btn" className="btn btn-forest w-full sm:w-auto">
        {state.sending && <Loader2 size={16} className="animate-spin" />} {state.sending ? c.sending : c.submit}
      </button>
    </form>
  );
};

export default function Contact() {
  const t = useT();
  const link = useLink();
  const c = t.contact;
  const items = [
    [Mail, c.labels.email, CONTACT.email, `mailto:${CONTACT.email}`],
    [Phone, c.labels.phone, CONTACT.phone, CONTACT.phoneHref],
    [MessageCircle, c.labels.whatsapp, CONTACT.phone, CONTACT.whatsappHref],
    [Instagram, c.labels.instagram, CONTACT.instagram, CONTACT.instagramHref],
    [MapPin, c.labels.location, t.footer.location, null],
  ];
  return (
    <div data-testid="contact-page">
      <PageHero eyebrow={c.eyebrow} title={c.title} intro={c.intro} image="/img/piran-church.jpg" imageAlt="Piran" />
      <section className="container-x grid gap-10 pb-24 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-5">
          {items.map(([Icon, label, value, href], i) => {
            const inner = (
              <>
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-forest text-gold-light"><Icon size={18} /></span>
                <span><span className="block text-xs uppercase tracking-[0.18em] text-gold-text">{label}</span><span className="mt-1 block text-base text-forest">{value}</span></span>
              </>
            );
            return (
              <Reveal key={label} delay={i * 0.04}>
                {href ? (
                  <a href={href} data-testid={`contact-detail-${i}`} className="card-soft flex items-center gap-4 p-5 transition-transform duration-300 hover:-translate-y-0.5" {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{inner}</a>
                ) : (
                  <div className="card-soft flex items-center gap-4 p-5" data-testid={`contact-detail-${i}`}>{inner}</div>
                )}
              </Reveal>
            );
          })}
          <div className="rounded-[28px] bg-forest p-7 text-ivory">
            <p className="text-[15px] leading-relaxed text-ivory/90">{c.consultNote}</p>
            <BtnLink to={link("apply")} variant="ivory" className="mt-5" data-testid="contact-apply-btn">{c.consultCta ?? t.common.apply}</BtnLink>
          </div>
        </div>
        <Reveal className="lg:col-span-7" delay={0.08}>
          <div className="card-soft p-8 sm:p-12">
            <h2 className="mb-8 font-display text-4xl text-forest">{c.formTitle}</h2>
            <ContactForm />
          </div>
        </Reveal>
      </section>
      {c.office && <OfficeSection o={c.office} testid="contact-office" className="container-x pb-24 sm:pb-28" />}
    </div>
  );
}
